import { CardBackgroundColorType, GradientType } from '~types'

type LayoutDirection = 'horizontal' | 'vertical'

type Props = {
  gradient?: GradientType
  color?: CardBackgroundColorType
  overrideColor?: CardBackgroundColorType | null
  direction: LayoutDirection
  isBelowBreakpoint?: boolean
  hasMediaInRightColumn?: boolean
  isMediaRight: boolean
}

type BackgroundStyle = {
  background?: string
  backdropFilter?: string
}

const COLOR_MAP: Record<CardBackgroundColorType, string> = {
  tealDark: '#003C4E',
  pinkLight: '#E86DB2',
  greenLight: '#7CCC6C',
  dark: '#1b1b1b',
  gold: '#41361D',
  purple: '#352036',
  transparent: 'transparent'
}

const GRADIENT_BUILDERS = {
  radial: (position: string, direction: string, color: string) =>
    `radial-gradient(${position}, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 100%),
     linear-gradient(${direction}, ${color} 0%, #000000 90%)`,

  linear: (direction: string, color: string) => `linear-gradient(${direction}, rgba(0, 0, 0, 0.15) 0%, ${color} 100%)`
}

const GRADIENT_PRESETS = {
  horizontal: {
    radial_gradient: {
      withMediaRight: (color: string) => GRADIENT_BUILDERS.radial('15% 0% at 0% 100%', '90deg', color),
      default: (color: string) => GRADIENT_BUILDERS.radial('15% 0% at 100% 100%', '270deg', color)
    },
    linear_gradient: {
      withMediaRight: (color: string) => GRADIENT_BUILDERS.linear('270deg', color),
      default: (color: string) => GRADIENT_BUILDERS.linear('90deg', color)
    },
    frosted_glass: () => `rgba(0, 0, 0, 0.1)`,
    transparent: () => 'transparent'
  },
  vertical: {
    radial_gradient: (color: string) => GRADIENT_BUILDERS.radial('79% 68% at 100% 100%', '0deg', color),
    linear_gradient: (color: string) => GRADIENT_BUILDERS.linear('174deg', color),
    frosted_glass: () => `rgba(0, 0, 0, 0.1)`,
    transparent: () => 'transparent'
  }
}

export const getBackgroundGradient = ({
  gradient,
  color,
  overrideColor,
  direction = 'horizontal',
  isMediaRight
}: Props): BackgroundStyle | null => {
  if (!color) return { background: COLOR_MAP.transparent }

  const baseColor = overrideColor ? COLOR_MAP[overrideColor] : COLOR_MAP[color]

  // Transparent or empty gradient returns plain hex color
  if (!gradient || gradient === 'transparent') {
    return { background: baseColor }
  }

  // "Frosted glass" receives blur treatment
  if (gradient === 'frosted_glass') {
    return {
      background: 'rgba(0, 0, 0, 0.1)',
      backdropFilter: 'blur(4px)'
    }
  }

  const gradientConfig = GRADIENT_PRESETS[direction][gradient]

  // Horizontal (object with withMediaRight/default)
  if (typeof gradientConfig === 'object') {
    return {
      background: isMediaRight ? gradientConfig.withMediaRight(baseColor) : gradientConfig.default(baseColor)
    }
  }

  // Vertical (function using color)
  return { background: (gradientConfig as (color: string) => string)(baseColor) }
}
